<script lang="ts">
  import {
    Grid,
    GridItem,
    type GridColumns,
    type ResponsiveGridColumns,
    type Spacing,
  } from '@athena-lib/Grid';
  import clsx from 'clsx';
  import Image from '@athena-lib/Image/Image.svelte';
  import { getColorContext } from '@athena-lib/container-context.svelte';
  import { Text } from '@athena-lib/Text';
  import { objectEntries } from '@athena-lib/object-utils';
  import { type Responsive } from '@athena-lib/responsive-utils';
  import type { HTMLAttributes } from 'svelte/elements';
  import type { DeviceName } from '@athena-lib/device';
  import type { ListGroupOption } from '../../ListGroupSelectable/types';

  type CardListProps = {
    options: ListGroupOption[];
    disabled: boolean;
    disabledTranslation?: string;
    class?: string | undefined;
    columns?: GridColumns | ResponsiveGridColumns;
    space?: Spacing | Partial<Record<DeviceName, Spacing>>;
    onChange: (option: ListGroupOption) => void;
  } & Omit<HTMLAttributes<HTMLDivElement>, 'class' | 'onchange' | 'disabled'>;

  const {
    options,
    disabled,
    disabledTranslation,
    columns = {
      sm: 1,
      md: 1,
      lg: 3,
    },
    space = {
      sm: 'small',
      md: 'small',
      lg: 'small',
    },
    class: className,
    onChange,
    ...restProps
  }: CardListProps = $props();

  const contextBackground = getColorContext();
  const colors = $derived.by(() => {
    const responsiveGridItemSelected: Responsive<string> = {
      lg: contextBackground.value.lg === 'dark' ? 'lg:bg-st-gray-700' : 'lg:bg-st-gray-200',
      md: contextBackground.value.md === 'dark' ? 'md:bg-st-gray-700' : 'md:bg-st-gray-200',
      sm: contextBackground.value.sm === 'dark' ? 'bg-st-gray-700' : 'bg-st-gray-200',
    };
    const responsiveGridItemNotSelected: Responsive<string> = {
      lg:
        contextBackground.value.lg === 'dark'
          ? 'lg:border-1 lg:border lg:border-st-gray-800 lg:bg-st-gray-800'
          : 'lg:border-1 lg:border lg:border-st-gray-300 lg:bg-st-white',
      md:
        contextBackground.value.md === 'dark'
          ? 'md:border-1 md:border md:border-st-gray-800 md:bg-st-gray-800'
          : 'md:border-1 md:border md:border-st-gray-300 md:bg-st-white',
      sm:
        contextBackground.value.sm === 'dark'
          ? 'border-1 border border-st-gray-800 bg-st-gray-800'
          : 'border-1 border border-st-gray-300 bg-st-white',
    };
    return {
      gridItemSelected: objectEntries(responsiveGridItemSelected)
        .map(([_, value]) => value)
        .join(' '),
      gridItemNotSelected: objectEntries(responsiveGridItemNotSelected)
        .map(([_, value]) => value)
        .join(' '),
    };
  });

  $effect(() => {
    options.forEach((option) => {
      const titleElement = document.getElementById(option.value.toString());
      if (!titleElement) {
        return;
      }
      const containerWidth = titleElement.offsetWidth;
      const textWidth = titleElement.scrollWidth;
      if (textWidth > containerWidth) {
        titleElement.classList.add('animate-scroll-text');
      } else {
        titleElement.classList.remove('animate-scroll-text');
      }
    });
  });
  function isSelected(option: ListGroupOption): boolean {
    return !!option.selected;
  }

  function isDisabled(option: ListGroupOption): boolean {
    return disabled || !!option.disabled;
  }

  function autoselect(event: Event, selectedOption: ListGroupOption): void {
    if (selectedOption.selected || isDisabled(selectedOption)) {
      event.stopPropagation();

      return;
    }

    const option = { ...selectedOption, selected: !isSelected(selectedOption) };
    if (onChange) {
      onChange(option);
    }
  }
</script>

<Grid {...restProps} {columns} {space} class={clsx('overflow-auto', className)}>
  {#each options as option (option.value)}
    <GridItem
      class={clsx(
        option.selected ? colors.gridItemSelected : colors.gridItemNotSelected,
        {
          'h-18 grid-cols-12 content-center overflow-y-hidden': options.length === 1,
        },
        'grid w-auto min-w-32 items-center justify-center rounded-lg lg:min-w-28',
      )}
    >
      <button
        type="button"
        class={clsx(
          {
            'col-span-full flex': options.length === 1,
            'cursor-default': option.disabled,
          },
          'truncate p-3',
        )}
        onclick={(e: Event) => autoselect(e, option as ListGroupOption)}
      >
        {#if option.disabled}
          <div class="bg-red-400 text-center">
            <Text color="light" size="xsmall" element="span" bold={true}>
              {disabledTranslation ?? ''}
            </Text>
          </div>
        {/if}
        {#if option.image}
          <div class="justify-self-center">
            <Image
              imgClass={clsx(
                { 'w-6 scale-150': options.length === 1 },
                'max-w-24 aspect-square object-cover',
              )}
              loading="lazy"
              src={option.image.url}
              alt={option.image.alt}
            ></Image>
          </div>
        {/if}
        <Text
          bold={option.selected}
          id={option.value.toString()}
          color={option.disabled ? 'medium' : 'dark'}
          class={clsx({
            'ps-5': options.length === 1,
          })}>{option.title}</Text
        >
      </button>
    </GridItem>
  {/each}
</Grid>
